<template>
	<Transition name="fade">
		<div :class="['popover', hasVariation]" ref="popover" v-show="display">
			<div class="popover__text" v-if="text">
				<slot name="text">{{ text }}</slot>
			</div>

			<div class="popover__sub-text" v-if="subText">
				<slot name="subText">{{ subText }}</slot>
			</div>

			<div class="popover__arrow" ref="arrow" />
			<PopperPosition
				:trackedRefName="trackedRef"
				:popoverRefName="$refs.popover"
				:arrowRefName="$refs.arrow"
				:placement="position"
				:popperVisible="display"
				:offset="[0, 16]"
				strategy="fixed"
			/>
		</div>
	</Transition>
</template>

<script>
import componentPositionMixin from '@/mixins/component-position-mixin';
import PopperPosition from '@/components/visual/popper-position';

export default {
	name: 'Popover',
	model: {
		prop: 'display',
		event: 'show'
	},
	components: {
		PopperPosition
	},
	mixins: [componentPositionMixin],
	props: {
		variation: {
			type: String,
			default: ''
		},
		text: {
			type: String,
			default: ''
		},
		subText: {
			type: String,
			default: ''
		},
		timedDismissable: {
			type: Boolean,
			default: true
		},
		display: {
			type: Boolean,
			default: false
		}
	},
	computed: {
		hasVariation() {
			return this.variation
				? `popover--${this.variation}`
				: this.variation;
		}
	},
	methods: {
		closeWindow() {
			this.$emit('show', false);
		}
	},
	watch: {
		display: {
			immediate: true,
			handler(val) {
				if (val) {
					if (this.timedDismissable === true) {
						setTimeout(() => {
							this.closeWindow();
						}, 3000);
					}
				}
			}
		}
	}
};
</script>

<style lang="scss" scoped>
/**
    * Custom Popover
    * Can use SCSS variables = TRUE
		* [Table of Contents]
    *
    * BASE
*/

.popover {
	width: max-content;
	max-width: 180px;
	background-color: $white;
	padding: $padding-md $padding-lg;
	border-radius: 5px;
	filter: drop-shadow(0 0 3px rgba(0, 0, 0, 0.2));

	@include mq($from: sm-l) {
		max-width: 240px;
	}

	@include mq($from: md) {
		max-width: 290px;
	}

	&::after {
		position: absolute;
		z-index: -1;
		content: '';
		border-style: solid;
		border-width: 0 12px 12px 12px;
		border-color: transparent transparent $white transparent;
	}

	&__text,
	&__sub-text {
		font-size: $label-font-size;
		word-break: break-word;
	}
	&__sub-text {
		margin-top: $gutter-lg;
	}
}

/* ARROW STYLES */
.popover {
	$arrowSizeBase: 12px; // Change this value to adjust the size of the arrow
	$arrowSizeA: #{$arrowSizeBase};
	$arrowSizeB: #{$arrowSizeBase * 2};

	&__arrow {
		width: $arrowSizeB;
		height: $arrowSizeA;
		position: absolute;
		top: 100%;
		left: 50%;

		&::before,
		&::after {
			position: absolute;
			display: block;
			content: '';
			border-style: solid;
			border-width: $arrowSizeA $arrowSizeA 0 $arrowSizeA;
			border-color: transparent;
		}

		&::before {
			top: 0;
			border-top-color: $white;
		}

		&::after {
			top: -1px;
			border-top-color: $white;
		}
	}

	&[data-popper-placement='bottom'] {
		.popover__arrow {
			top: -$arrowSizeA;
			left: 50%;

			&::before,
			&::after {
				border-color: transparent;
				border-width: 0 $arrowSizeA $arrowSizeA $arrowSizeA;
			}

			&::before {
				top: 0;
				border-bottom-color: $white;
			}

			&::after {
				top: 1px;
				border-bottom-color: $white;
			}
		}
	}

	&[data-popper-placement='left'] {
		.popover__arrow {
			left: 100%;
			width: $arrowSizeA;
			height: $arrowSizeB;

			&::before,
			&::after {
				border-width: $arrowSizeA 0 $arrowSizeA $arrowSizeA;
				border-top-color: transparent;
			}

			&::before {
				top: initial;
				left: 0;
				border-left-color: $white;
			}

			&::after {
				top: initial;
				left: -1px;
				border-left-color: $white;
			}
		}
	}

	&[data-popper-placement='right'] {
		.popover__arrow {
			left: -$arrowSizeA;
			width: $arrowSizeA;
			height: $arrowSizeB;

			&::before,
			&::after {
				border-width: $arrowSizeA $arrowSizeA $arrowSizeA 0;
				border-top-color: transparent;
			}

			&::before {
				top: initial;
				left: 0px;
				border-right-color: $white;
			}

			&::after {
				top: initial;
				left: 1px;
				border-right-color: $white;
			}
		}
	}
}
</style>
